import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Fade, Typography, Box, Button, IconButton, Paper, styled } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { SimpleTreeView, TreeItem, treeItemClasses } from '@mui/x-tree-view';
import { ArrowRight, Delete, Edit } from '@mui/icons-material';
import { ArrowDropDownIcon } from '@mui/x-date-pickers';
import { useThemes } from '../../../../components/Theme/theme';
import { useCreateItemCategorymasterMutation } from '../../../../redux/apis/itemCategory.api';
import { dashboardApi } from '../../../../redux/apis/dashboardApi';
import { showErrorSnackbar, showSuccessSnackbar } from '../../../../components/snackBar/snakbar';
import AddCategory from '../../../../components/modals/AddCategory';

const style = {
  overflow: 'auto',
  position: 'absolute' as const,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  boxShadow: 24,
  outline: 'none',
  '@media (min-width:600px)': {
    width: 'auto',
  },
  width: '90%',
};

const CustomExpandIcon = styled(ArrowRight)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '25px !important',
}));

const CustomCollapseIcon = styled(ArrowDropDownIcon)(({ theme }) => ({
  color: theme.palette.primary.main,
  fontSize: '25px !important',
}));

const CustomTreeItem = styled(TreeItem)(({ theme }) => ({
  [`& .${treeItemClasses.content}`]: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    height: '40px',
    position: 'relative',
    '&:before': {
      content: '""',
      position: 'absolute',
      left: -10,
      bottom: '50%',
      transform: 'translateY(50%)',
      width: 10,
    },
  },
  [`& .${treeItemClasses.groupTransition}`]: {
    marginTop: -8,
    marginLeft: 13,
    paddingLeft: 8,
    position: 'relative',
  },
  [`& .${treeItemClasses.root}`]: {
    '&:last-child .${treeItemClasses.groupTransition}': {
      borderLeft: 'none',
    },
  },
  [`& .singleChild .${treeItemClasses.groupTransition}`]: {
    '&:before': {
      content: '""',
      position: 'absolute',
      left: -8,
      top: -12,
      height: 12,
    },
  },
}));

interface CategoryItem {
  label: string;
  value: number;
  itemcategoryid: number;
  category: string;
  itemcategorygroupid: number;
  maincategory: string;
  parent: string | null;
  children: CategoryItem[];
  descendantCount?: number;
}

interface RecursiveListItemProps {
  item: CategoryItem;
  deleteCategory: (item: CategoryItem) => void;
  rows: CategoryItem[];
  listCategory: () => void;
}

const RecursiveListItem: React.FC<RecursiveListItemProps> = ({ item, deleteCategory, rows, listCategory }) => {
  const [editModal, setEditModal] = useState(false);
  const [editDetails, setEditDetails] = useState<{
    parent?: CategoryItem;
    child?: CategoryItem;
  } | null>(null);

  return (
    <>
      <CustomTreeItem
        itemId={String(item.value)}
        label={
          <Box sx={{ width: '100%', display: 'flex' }}>
            <Box
              sx={{
                width: '100%',
                display: '-webkit-box',
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                WebkitLineClamp: 2,
                lineHeight: '1.5',
                maxHeight: '3em',
                fontSize: '14px',
              }}
            >
              {item.label}
            </Box>
            {item.parent !== null ? (
              <Box
                sx={{
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <IconButton
                  sx={{
                    margin: '0px 2px',
                    padding: '5px',
                    backgroundColor: '#D0EFFF',
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setEditModal(true);
                    const parentDetails = rows.find((e) => e.value === item.itemcategorygroupid);
                    setEditDetails({ parent: parentDetails, child: item });
                  }}
                >
                  <Edit
                    sx={{
                      fontSize: '15px',
                      color: '#6096d9',
                    }}
                  />
                </IconButton>
                <IconButton
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteCategory(item);
                  }}
                  sx={{
                    margin: '0px 2px',
                    padding: '5px',
                    backgroundColor: '#fbc7c7',
                  }}
                >
                  <Delete
                    sx={{
                      fontSize: '15px',
                      color: '#eb6a6a',
                    }}
                  />
                </IconButton>
              </Box>
            ) : null}
          </Box>
        }
      >
        {item?.children.length !== 0 ? (
          <>
            {item?.children?.map((child) => (
              <RecursiveListItem key={child.value} item={child} deleteCategory={deleteCategory} rows={rows} listCategory={listCategory} />
            ))}
          </>
        ) : null}
      </CustomTreeItem>
      {editModal ? (
        <AddCategory
          open={editModal}
          setOpen={setEditModal}
          MainParent={editDetails?.child?.maincategory}
          setCheckedLabel={undefined}
          setStateS={undefined}
          params={undefined}
          addparents={undefined}
          dataArr={undefined}
          setCategoryData={undefined}
          setCloseEvent={undefined}
          FnAftrClose={undefined}
          IDparent={undefined}
          editDetails={editDetails}
          listCategory={listCategory}
        />
      ) : null}
    </>
  );
};

interface DeleteCategoryModalProps {
  index?: number;
  setrow?: (prev: any) => any;
  setState?: (prev: any) => any;
  open: boolean;
  setOpen: (open: boolean) => void;
  params?: any;
  setCategoryArr?: (arr: CategoryItem[]) => void;
  categoryArr: CategoryItem[];
  setCategoryModal?: (open: boolean) => void;
  listCategory: () => void;
  FnAftrClose?: () => void;
}

const DeleteCategoryModal: React.FC<DeleteCategoryModalProps> = ({
  index,
  setrow,
  setState,
  open,
  setOpen,
  params,
  setCategoryArr,
  categoryArr,
  setCategoryModal,
  listCategory,
  FnAftrClose,
}) => {
  const dispatch = useDispatch();
  const { theme } = useThemes();
  const [loading, setLoading] = useState(false);
  const [group, setGroup] = useState<CategoryItem | null>(null);
  const [deleteModal, setDeleteModal] = useState(false);
  const [rows, setRows] = useState<CategoryItem[]>([]);
  const [expandIds, setExpandIds] = useState<string[]>([]);

  const [createCategory] = useCreateItemCategorymasterMutation();

  const handleClose = useCallback(
    (event?: object, reason?: string) => {
      if (reason !== 'backdropClick') {
        setOpen(false);
      }
    },
    [setOpen]
  );

  const handleConfirmClose = useCallback((event?: object, reason?: string) => {
    if (reason !== 'backdropClick') {
      setDeleteModal(false);
    }
  }, []);

  const handleNewGroup = useCallback(() => {
    handleClose();
    if (setCategoryModal) {
      setCategoryModal(true);
    }
  }, [handleClose, setCategoryModal]);

  const countDescendants = useCallback((node: CategoryItem): number => {
    if (!node.children || node.children.length === 0) {
      return 0;
    }
    let count = node.children.length;
    node.children.forEach((child) => {
      count += countDescendants(child);
    });
    return count;
  }, []);

  const sortTree = useCallback(
    (nodes: CategoryItem[]): void => {
      nodes.forEach((node) => {
        node.descendantCount = countDescendants(node);
      });
      nodes.sort((a, b) => (b.descendantCount ?? 0) - (a.descendantCount ?? 0));
      nodes.forEach((node) => {
        if (node.children && node.children.length > 0) {
          sortTree(node.children);
        }
      });
    },
    [countDescendants]
  );

  const flattenTree = useCallback((tree: CategoryItem[]): CategoryItem[] => {
    let flat: CategoryItem[] = [];
    tree.forEach((node) => {
      flat.push(node);
      if (node.children && node.children.length) {
        flat = flat.concat(flattenTree(node.children));
      }
    });
    return flat;
  }, []);

  const addParentKey = useCallback(
    (data: CategoryItem[]): CategoryItem[] => {
      const idToLabel = data.reduce((acc, item) => {
        acc[item.itemcategoryid] = item.label;
        return acc;
      }, {} as Record<number, string>);

      data.forEach((item) => {
        item.parent = idToLabel[item.itemcategorygroupid] || null;
      });

      const buildTree = (dataItems: CategoryItem[]): CategoryItem[] => {
        const tree: CategoryItem[] = [];
        const childrenOf: Record<number, CategoryItem[]> = {};
        dataItems.forEach((item) => {
          item.children = childrenOf[item.itemcategoryid] = childrenOf[item.itemcategoryid] || [];
          const parentID = item.itemcategorygroupid;
          if (parentID && idToLabel[parentID]) {
            (childrenOf[parentID] = childrenOf[parentID] || []).push(item);
          } else {
            tree.push(item);
          }
        });
        sortTree(tree);
        return tree;
      };

      const tree = buildTree(data);
      const ids = flattenTree(tree)
        .filter((e) => e.children.length !== 0)
        .map((el) => String(el.value));
      setExpandIds(ids.length === 0 ? [String(flattenTree(tree)[0]?.value)] : ids);
      return buildTree(data);
    },
    [sortTree, flattenTree]
  );

  useEffect(() => {
    const sorted = [...categoryArr].sort(function (a, b) {
      if (a.label < b.label) {
        return -1;
      }
      if (a.label > b.label) {
        return 1;
      }
      return 0;
    });
    setRows(addParentKey(sorted));
  }, [open, categoryArr, addParentKey]);

  const deleteMethod = useCallback(async () => {
    if (!group) return;

    try {
      setLoading(true);
      const data = {
        maincategory: group.maincategory,
        category: group.category,
        expiredtimestamp: Math.floor(Date.now() / 1000),
        itemcategorygroupid: group.itemcategorygroupid,
        itemcategoryid: group.itemcategoryid,
      };

      const result = await createCategory({ createItemCategorymasterInput: data });

      if ('error' in result) {
        throw new Error('Failed to delete category');
      }

      const responseData = result.data?.createItemCategorymaster;

      if (responseData?.statuscode === 'Error') {
        setLoading(false);
        setDeleteModal(false);
        if (setState) {
          setState((prev: any) => ({
            ...prev,
            itemCtegorey: group.itemcategoryid,
          }));
        }
        showErrorSnackbar(responseData.statusmessage || 'Failed to delete category');
      } else {
        if (responseData != null) {
          setOpen(false);
          setLoading(false);
          setDeleteModal(false);
          showSuccessSnackbar('Category Deleted');
          dispatch(dashboardApi.util.invalidateTags(['Dashboard_itemCategoryMaster']));
          listCategory();
        }
      }
    } catch (error) {
      console.log(error);
      setLoading(false);
      setDeleteModal(false);
    }
  }, [group, createCategory, dispatch, setState, setOpen, listCategory]);

  const deleteGroup = useCallback(
    (groupData: CategoryItem) => {
      if (setrow && index !== undefined) {
        setrow((prevArray: any[]) => {
          const newArray = [...prevArray];
          const firstObject = { ...newArray[index] };
          firstObject.category = '';
          newArray[index] = firstObject;
          return newArray;
        });
      }
      if (setState) {
        setState((prev: any) => ({
          ...prev,
          itemCtegorey: '',
        }));
      }
      setGroup(groupData);
      setDeleteModal(true);
    },
    [setrow, setState, index]
  );

  return (
    <Modal
      id={open ? 'deleteCategory' : ''}
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      closeAfterTransition
      onClose={handleClose}
    >
      <Fade in={open}>
        <Paper
          style={{ boxShadow: 'none' }}
          sx={{
            ...style,
            width: { xs: '90%', sm: '75%', md: '450px' },
          }}
        >
          <Box
            sx={{
              backgroundColor: 'primary.main',
              padding: '5px 15px',
              display: 'flex',
              flexDirection: 'row',
              position: 'sticky',
              zIndex: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Typography
                sx={{
                  color: '#fff',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                Category List
                <IconButton aria-label="delete" onClick={() => handleClose({}, 'closeClick')}>
                  <CloseIcon sx={{ color: '#fff' }} />
                </IconButton>
              </Typography>
            </Box>
          </Box>

          <Box
            className="custom-scrollbar"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              height: '400px',
              padding: '15px',
              overflowX: 'hidden',
            }}
          >
            <Typography
              variant="h6"
              component="div"
              sx={{
                justifyContent: 'space-between',
                color: '#595C66',
                borderTopRightRadius: 4,
                borderTopLeftRadius: 4,
                p: 1,
                backgroundColor: 'background.tablebg',
                display: 'flex',
              }}
              gutterBottom
            >
              <Typography sx={{ fontWeight: 'bold' }} align="left">
                Category
              </Typography>
              <Typography sx={{ fontWeight: 'bold' }} align="left">
                Actions
              </Typography>
            </Typography>
            {expandIds.length !== 0 ? (
              <SimpleTreeView
                aria-label="customized"
                defaultExpandedItems={expandIds}
                slots={{
                  expandIcon: CustomExpandIcon,
                  collapseIcon: CustomCollapseIcon,
                }}
                sx={{
                  flexGrow: 1,
                  width: '100%',
                }}
              >
                {rows?.map((item) => (
                  <RecursiveListItem
                    key={item.value}
                    item={item}
                    deleteCategory={deleteGroup}
                    rows={categoryArr}
                    listCategory={() => {
                      listCategory();
                      addParentKey(categoryArr);
                    }}
                  />
                ))}
              </SimpleTreeView>
            ) : null}
          </Box>

          <Modal
            aria-labelledby="transition-modal-title"
            aria-describedby="transition-modal-description"
            open={deleteModal}
            onClose={handleConfirmClose}
            closeAfterTransition
          >
            <Paper sx={style}>
              <Box
                sx={{
                  borderRadius: '4px',
                  padding: '15px',
                  textAlign: 'center',
                  overflowY: { xs: 'scroll', sm: 'hidden' },
                  '.MuiTableCell-root': {
                    padding: '6px 10px',
                    fontSize: '13px',
                  },
                }}
              >
                <Typography
                  sx={{
                    color: (theme?.palette as any)?.TmId?.id?.toString().includes('9') ? 'white' : 'rgb(60 60 60)',
                    fontWeight: 'bold',
                  }}
                >
                  Do you want to delete this Category?
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  flexDirection: 'row',
                  paddingLeft: '15px',
                  paddingRight: '15px',
                  paddingBottom: '15px',
                }}
              >
                <Button
                  onClick={() => handleConfirmClose({}, 'closeClick')}
                  variant="outlined"
                  disabled={loading}
                  sx={{ flex: 1, margin: '5px', fontSize: '12px' }}
                >
                  No
                </Button>
                <LoadingButton
                  loading={loading}
                  color="error"
                  disableRipple={true}
                  onClick={deleteMethod}
                  variant="contained"
                  sx={{ flex: 1, margin: '5px', fontSize: '12px' }}
                >
                  Yes
                </LoadingButton>
              </Box>
            </Paper>
          </Modal>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button
              onClick={() => handleClose({}, 'closeClick')}
              disabled={loading}
              variant="outlined"
              sx={{ flex: 1, margin: '10px', fontSize: '12px' }}
            >
              Cancel
            </Button>
            <Button onClick={handleNewGroup} disabled={loading} variant="contained" sx={{ flex: 1, margin: '10px', fontSize: '12px' }}>
              Add New Category
            </Button>
          </Box>
        </Paper>
      </Fade>
    </Modal>
  );
};

export default DeleteCategoryModal;
